/**
 * Declarative, version-controlled configuration for browser error-tracking
 * noise reduction. This is the file to diff over time as new noise appears or
 * old workarounds become obsolete — the logic that consumes it lives in
 * `errorEventFilter.ts` and `botDetection.ts` and should not need to change.
 */

import { originPatternForDomain } from './errorEventFilter';
import type { ErrorEventFilterConfig, IgnoredMessagePattern } from './errorEventFilter';

/**
 * Origins we own. An error is kept only if its stack points at one of these
 * (when the stack has any URL at all). Ported from the old Sentry `allowUrls`.
 * This is the blunt instrument that drops the entire third-party / extension /
 * ad / scraper class without per-message rules.
 */
export const ALLOWED_ERROR_ORIGINS: RegExp[] = [
  originPatternForDomain('reverb.com'),
  originPatternForDomain('reverb-assets.com'),
];

/**
 * Messages to drop regardless of origin. Kept intentionally short: the origin
 * allowlist above handles most third-party noise generically, so this list is
 * reserved for (a) noise that originates from our OWN origin, and (b) injected
 * errors whose stack has no usable URL for the allowlist to match.
 *
 * Ported selectively from the legacy Sentry `ignoreErrors`.
 */
export const IGNORED_MESSAGE_PATTERNS: IgnoredMessagePattern[] = [
  // Google Publisher Tag (GPT) ad library — the googletag global is defined by
  // securepubads.g.doubleclick.net/tag/js/gpt.js. These fire from injected/eval'd
  // ad code with an anonymous stack (no URL), so the origin allowlist cannot catch
  // them — must match by message. Datadog issue 50e45a2c-fdfe-11f0-b1c0-da7ad0900002.
  //
  // `googletag is not defined`: ~13.7M events / 30d — the single highest-volume
  // client error (~1.3% of all reverb-ui-client errors). Also covers the
  // googletag.cmd / .pubads / .defineSlot exception family (all contain "googletag").
  /\bgoogletag\b/,

  // GPT queued-command failures where an ad creative references an undefined
  // global. The identifier is arbitrary and varies per creative (seen: `scoop`,
  // `PH`), so match the GPT wrapper, not the name. ~400 events / 30d combined.
  /Exception in queued GPT command/,

  'UET is not defined', // Bing UET tag, injected by tracking scripts

  // Our own origin, known-benign noise.
  /Loading chunk/, // code-split chunk fetch failures on flaky connections
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
];

/** Single config object consumed by `createErrorEventBeforeSend`. */
export const ERROR_EVENT_FILTER_CONFIG: ErrorEventFilterConfig = {
  allowedOrigins: ALLOWED_ERROR_ORIGINS,
  ignoredMessagePatterns: IGNORED_MESSAGE_PATTERNS,
};

/**
 * User-agent substrings/patterns for self-identifying automation. Matched
 * case-insensitively. This catches the high-volume, honest bots (crawlers,
 * headless browsers); spoofed scrapers are handled downstream by the filter.
 */
export const BOT_USER_AGENT_PATTERNS: RegExp[] = [
  /bot/i,
  /crawl/i,
  /spider/i,
  /headless/i,
  /bytespider/i,
  /slurp/i,
  /bingpreview/i,
  /lighthouse/i,
  /pingdom/i,
  /datadog/i, // Datadog Synthetics
];
